// Our team: who can sign in to the console.

import { useState, type FormEvent } from 'react';
import { api, message } from '../api.ts';
import { ago } from '../format.ts';
import type { StaffRow } from '../types.ts';
import { Dialog, Loading, Problem, Shell, useLoad, useMe } from '../ui.tsx';

export function Team() {
  const { person } = useMe();
  const staff = useLoad(() => api<{ staff: StaffRow[] }>('GET', '/staff'), []);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async (s: StaffRow) => {
    if (!confirm(`Remove ${s.name}? Their passkeys stop working straight away.`)) return;
    try {
      await api('DELETE', `/staff/${s.staffId}`);
      staff.reload();
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <Shell>
      <div className="head">
        <div className="head-title">
          <h1>Team</h1>
          <p className="muted">Everyone who can sign in to the console. Each person uses their own passkey.</p>
        </div>
        <button className="btn primary" onClick={() => setInviting(true)}>
          Invite someone
        </button>
      </div>
      {(staff.error || error) && <Problem text={(staff.error ?? error) as string} />}
      {staff.loading && !staff.data && <Loading />}
      {staff.data && (
        <div className="table" style={{ maxWidth: 1000 }}>
          <div className="tr th" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.3fr) 150px 160px 90px' }}>
            <span>Name</span>
            <span>Email</span>
            <span>Passkeys</span>
            <span>Last signed in</span>
            <span />
          </div>
          {staff.data.staff.map((s) => (
            <div key={s.staffId} className="tr" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.3fr) 150px 160px 90px', opacity: s.active ? 1 : 0.5 }}>
              <span>{s.name}</span>
              <span className="muted">{s.email}</span>
              <span className={s.passkeys ? 'green' : 'amber'}>{!s.active ? 'Removed' : s.passkeys ? `${s.passkeys} set up` : 'Invite sent'}</span>
              <span className="muted">{s.lastUsedAt ? ago(s.lastUsedAt) : '—'}</span>
              <span>
                {s.active && s.staffId !== person?.personId && (
                  <button className="linkish" onClick={() => remove(s)}>
                    Remove
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {inviting && <InviteStaff onClose={() => setInviting(false)} onDone={staff.reload} />}
    </Shell>
  );
}

function InviteStaff({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/staff', { name, email });
      onDone();
      onClose();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title="Invite someone to the team" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <p className="muted">They get an email link to set up a passkey. To add a passkey for a new phone of your own, invite yourself again.</p>
        <label className="field">
          Name
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>
        <label className="field">
          Email
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="row">
          <button className="btn primary" type="submit" disabled={busy}>
            Send the invite
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}
